(function() {
  const SHIPPING_ESTIMATE_TIMEOUT = 15000;

  function shippingSubtotal(cart) {
    const itemTotal = (cart?.items || [])
      .filter((item) => item.requires_shipping)
      .reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
    const discountTotal = (cart?.cart_level_discount_applications || []).reduce((sum, application) => {
      return sum + Number(application.total_allocated_amount ?? application.discount_application?.total_allocated_amount ?? 0);
    }, 0);

    return Math.max(0, itemTotal - discountTotal);
  }

  function interpolate(template, replacement) {
    return String(template || '').replace(/{{.*?}}/g, replacement);
  }

  function formatCurrency(amount, currency = 'CAD', locale = 'en-CA') {
    return new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol', minimumFractionDigits: Number.isInteger(amount) ? 0 : 2, maximumFractionDigits: 2 }).format(amount);
  }

  function formatShippingRate(rate, locale = 'en-CA') {
    const amount = Number(rate?.price);
    if (!Number.isFinite(amount) || amount === 0) return 'Free';
    const currency = rate.currency || 'CAD';
    return `${currency} ${formatCurrency(amount, currency, locale)}`;
  }

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
    })[character]);
  }

  class JomoOrderPerks extends HTMLElement {
    static get observedAttributes() {
      return ['threshold', 'total-price', 'static-message', 'reached-message', 'unreached-message'];
    }

    constructor() {
      super();
      this.onCartChanged = this.onCartChanged.bind(this);
    }

    connectedCallback() {
      document.addEventListener('cart:change', this.onCartChanged);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:change', this.onCartChanged);
    }

    attributeChangedCallback() {
      if (this.isConnected) this.render();
    }

    setDeliveryThreshold(value) {
      this.setAttribute('threshold', String(Math.max(0, Number(value) || 0)));
    }

    onCartChanged(event) {
      this.setAttribute('total-price', String(shippingSubtotal(event.detail?.cart)));
    }

    render() {
      const threshold = Math.max(0, Number(this.getAttribute('threshold')) || 0);
      const totalPrice = Math.max(0, Number(this.getAttribute('total-price')) || 0);
      const staticMessage = this.getAttribute('static-message');
      const isReached = totalPrice >= threshold;
      const copy = this.querySelector('[data-delivery-message]') || this.querySelector('.order-perks__copy');
      const shippingInfo = this.querySelector('jomo-shipping-info');
      const progress = this.querySelector('progress-bar');
      const unreachedIcon = this.querySelector('[data-delivery-icon-unreached]');
      const reachedIcon = this.querySelector('[data-delivery-icon-reached]');

      if (progress) {
        progress.hidden = Boolean(staticMessage);
        progress.setAttribute('aria-valuemin', '0');
        progress.setAttribute('aria-valuenow', String(totalPrice / 100));
        progress.setAttribute('aria-valuemax', String(threshold / 100));
      }

      if (unreachedIcon) unreachedIcon.hidden = staticMessage ? false : isReached;
      if (reachedIcon) reachedIcon.hidden = staticMessage ? true : !isReached;
      if (!copy) return;

      if (staticMessage) {
        copy.textContent = staticMessage;
      } else if (isReached) {
        copy.innerHTML = this.getAttribute('reached-message') || '';
      } else {
        const currency = window.Shopify?.currency?.active || 'CAD';
        const locale = window.Shopify?.locale || document.documentElement.lang || 'en-CA';
        const remaining = formatCurrency((threshold - totalPrice) / 100, currency, locale);
        copy.innerHTML = interpolate(this.getAttribute('unreached-message'), remaining);
      }
      shippingInfo?.positionPopover?.();
    }
  }

  class JomoShippingInfo extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.trigger = this.querySelector('button');
      this.status = this.querySelector('[data-shipping-info-status]');
      this.trigger?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.openChecker();
      }, { signal: this.controller.signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
      queueMicrotask(() => {
        if (!this.isConnected) this.deliveryPopover?.releaseTrigger(this.trigger);
      });
    }

    async openChecker() {
      if (this.loading) return;
      this.loading = true;
      this.trigger.setAttribute('aria-busy', 'true');
      if (this.status) this.status.textContent = '';
      try {
        const { openDeliveryChecker } = await import(this.dataset.moduleUrl);
        if (this.isConnected) this.deliveryPopover = openDeliveryChecker(this.trigger);
      } catch (error) {
        if (this.isConnected && this.status) this.status.textContent = this.status.dataset.errorMessage;
        console.warn('[Delivery checker] Could not open', error);
      } finally {
        this.loading = false;
        this.trigger.removeAttribute('aria-busy');
      }
    }

    positionPopover() {
      this.deliveryPopover?.positionPanel();
    }
  }

  class GiftNote extends HTMLElement {
    constructor() {
      super();
      this.saveQueue = Promise.resolve();
      this.addEventListener('change', (event) => this.enqueueSave(event));
    }

    enqueueSave(event) {
      if (event.target.getAttribute('name') !== 'attributes[Gift Note]') return;
      if (this.hasSavedNote === undefined) {
        this.hasSavedNote = Boolean(event.target.defaultValue?.trim());
      }
      const value = event.target.value;
      this.saveQueue = this.saveQueue.then(() => this.save(value));
    }

    async save(value) {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { 'Gift Note': value } }),
          keepalive: true
        });
        if (!response.ok) throw new Error(`Cart update returned ${response.status}`);
        const hasNote = Boolean(value.trim());
        const added = hasNote && !this.hasSavedNote;
        this.hasSavedNote = hasNote;
        if (added) this.trackAddition();
      } catch (error) {
        console.warn('[Gift note] Could not save the note automatically.', error);
      }
    }
    trackAddition() {
      const eventData = { source: 'cart' };
      try {
        const published = window.Shopify?.analytics?.publish?.('jomo:gift_note_added', eventData);
        published?.catch?.(() => console.warn('[Gift note analytics] Event delivery failed.'));
      } catch {
        console.warn('[Gift note analytics] Event delivery failed.');
      }
      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'gift_note_added', eventData);
        } else if (Array.isArray(window.dataLayer)) {
          window.dataLayer.push({ event: 'gift_note_added', ...eventData });
        }
      } catch {
        console.warn('[Gift note analytics] Event delivery failed.');
      }
    }
  }

  class JomoShippingEstimator extends HTMLElement {
    constructor() {
      super();
      this.requestRevision = 0;
      this.requestController = null;
      this.isEstimating = false;
    }

    connectedCallback() {
      this.submitButton = this.querySelector('[type="button"]');
      this.zipInput = this.querySelector('[name="address[zip]"]');
      this.resultsElement = this.querySelector('[aria-live="polite"]');
      this.onEstimate = this.estimate.bind(this);
      this.onZipKeydown = this.handleZipKeydown.bind(this);
      this.submitButton?.addEventListener('click', this.onEstimate);
      this.zipInput?.addEventListener('keydown', this.onZipKeydown);
    }

    disconnectedCallback() {
      this.submitButton?.removeEventListener('click', this.onEstimate);
      this.zipInput?.removeEventListener('keydown', this.onZipKeydown);
      this.cancelEstimate();
    }

    handleZipKeydown(event) {
      if (event.key !== 'Enter' || event.isComposing || event.keyCode === 229) return;

      event.preventDefault();
      event.stopPropagation();
      if (event.repeat) return;

      return this.estimate(event);
    }

    get address() {
      return {
        zip: this.querySelector('[name="address[zip]"]')?.value || '',
        country: this.querySelector('[name="address[country]"]')?.value || '',
        province: this.querySelector('[name="address[province]"]')?.value || ''
      };
    }

    endpoint(path, address) {
      const params = new URLSearchParams({
        'shipping_address[zip]': address.zip,
        'shipping_address[country]': address.country,
        'shipping_address[province]': address.province
      });
      return `${window.Shopify?.routes?.root || '/'}cart/${path}?${params}`;
    }

    cancelEstimate() {
      this.requestRevision += 1;
      this.requestController?.abort();
      this.requestController = null;

      if (this.isEstimating) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        this.isEstimating = false;
      }

      if (this.submitButton) {
        this.submitButton.disabled = false;
        this.submitButton.removeAttribute('aria-busy');
      }
    }

    async estimate(event) {
      event.preventDefault();
      const address = this.address;
      this.cancelEstimate();
      const requestRevision = ++this.requestRevision;
      const requestController = new AbortController();
      this.requestController = requestController;
      const requestTimeout = setTimeout(() => requestController.abort(), SHIPPING_ESTIMATE_TIMEOUT);
      this.isEstimating = true;
      this.submitButton.disabled = true;
      this.submitButton?.setAttribute('aria-busy', 'true');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const prepared = await fetch(this.endpoint('prepare_shipping_rates.json', address), { method: 'POST', signal: requestController.signal });
        if (requestRevision !== this.requestRevision || !this.isConnected) return;

        if (!prepared.ok) {
          const errors = await prepared.json();
          if (requestRevision === this.requestRevision && this.isConnected) this.renderErrors(errors);
          return;
        }
        const rates = await this.pollRates(address, requestController.signal);
        if (requestRevision === this.requestRevision && this.isConnected) this.renderRates(rates);
      } catch (error) {
        if (requestRevision === this.requestRevision && this.isConnected) {
          this.renderErrors({ request: window.themeVariables?.strings?.shippingEstimatorError || error.message });
        }
      } finally {
        clearTimeout(requestTimeout);
        if (requestRevision === this.requestRevision) {
          this.requestController = null;
          this.isEstimating = false;
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
          if (this.resultsElement && this.isConnected) this.resultsElement.hidden = false;
          if (this.submitButton) {
            this.submitButton.disabled = false;
            this.submitButton.removeAttribute('aria-busy');
          }
        }
      }
    }

    async pollRates(address, signal) {
      for (let attempt = 0; attempt < 20; attempt += 1) {
        const response = await fetch(this.endpoint('async_shipping_rates.json', address), { signal });
        const text = await response.text();
        if (text !== 'null') return JSON.parse(text).shipping_rates || [];
        await new Promise((resolve) => setTimeout(resolve, 250));
      }
      throw new Error('Shipping rates timed out');
    }

    renderRates(rates) {
      const strings = window.themeVariables?.strings || {};
      const summary = rates.length === 0 ? strings.shippingEstimatorNoResults : rates.length === 1 ? strings.shippingEstimatorOneResult : strings.shippingEstimatorMultipleResults;
      const locale = window.Shopify?.locale || document.documentElement.lang || 'en-CA';
      const items = rates.map((rate) => `<li>${escapeHtml(rate.presentment_name)}: ${escapeHtml(formatShippingRate(rate, locale))}</li>`).join('');
      this.resultsElement.innerHTML = `<div class="v-stack gap-2"><p>${escapeHtml(summary)}</p>${items ? `<ul class="list-disc" role="list">${items}</ul>` : ''}</div>`;
    }

    renderErrors(errors) {
      const items = Object.values(errors || {}).map((message) => `<li>${escapeHtml(message)}</li>`).join('');
      this.resultsElement.innerHTML = `<div class="v-stack gap-2"><p>${escapeHtml(window.themeVariables?.strings?.shippingEstimatorError)}</p><ul class="list-disc" role="list">${items}</ul></div>`;
    }
  }

  if (!customElements.get('jomo-order-perks')) customElements.define('jomo-order-perks', JomoOrderPerks);
  if (!customElements.get('jomo-shipping-info')) customElements.define('jomo-shipping-info', JomoShippingInfo);
  if (!customElements.get('gift-note')) customElements.define('gift-note', GiftNote);
  if (!customElements.get('jomo-shipping-estimator')) customElements.define('jomo-shipping-estimator', JomoShippingEstimator);

  window.JomoCart = { shippingSubtotal, interpolate, formatCurrency, formatShippingRate, escapeHtml };
})();
